import { Box, Button, Collapse, Flex, Spinner, Stack, Text, Td, useDisclosure } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import {
  useGetContractSigningQuery,
  type AwaitingSignatureDto,
  type ContractReportQueryDto,
  type RecentlySignedDto,
} from "../../autogen/bff-api";
import { base64Encode } from "../../common/utils/base64";
import { SignedDocumentStatusTag } from "../../common/documents/signatures/SignedDocumentStatusTag";
import { useApiError } from "../../common/errors/useApiError";
import { displayDate } from "../../common/formatting/displayDate";
import { ListLoadError } from "../../common/ListLoadError";
import { urls } from "../../urls";
import { MutedCell, PanelRow, PanelTable, TitleCell } from "../home/panels/PanelTable";

export const ContractSigning = ({ q, base }: { q: string; base: ContractReportQueryDto }) => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, error, refetch } = useGetContractSigningQuery({ q });
  const { message } = useApiError();
  const isRefreshing = isFetching && !isLoading;

  if (isLoading) return <Spinner color="smPrimary" />;
  if (error || !data) {
    return <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />;
  }
  return (
    <Stack spacing="4" opacity={isRefreshing ? 0.6 : 1} aria-busy={isRefreshing} data-testid="contract-signing">
      <SectionCard
        title={t("Waiting for signing ({{count}} contracts)", { count: data.awaitingCount })}
        isRefreshing={isRefreshing}
        testId="awaiting-signature"
      >
        <AwaitingTable rows={data.awaiting} />
        {data.awaitingCount > data.awaiting.length && (
          <SeeAll to={registerWith(base, { awaitingSignature: true })} count={data.awaitingCount} />
        )}
      </SectionCard>
      <SectionCard
        title={t("Signed in the last 30 days ({{count}})", { count: data.recentlySignedCount })}
        isRefreshing={isRefreshing}
        testId="recently-signed"
      >
        <RecentlySigned rows={data.recentlySigned} />
        {data.recentlySignedCount > data.recentlySigned.length && (
          <SeeAll
            to={registerWith(base, { signedFrom: data.signedFrom, signedTo: data.signedTo })}
            count={data.recentlySignedCount}
          />
        )}
      </SectionCard>
      {data.activeWithoutSigning > 0 && (
        <Text fontSize="sm" color="smMuted">
          {base.activeFrameworkAgreement
            ? t(
                "{{count}} suppliers in active framework agreements were uploaded already signed or have no signing in the product, and are not included in «Signed in the last 30 days».",
                { count: data.activeWithoutSigning },
              )
            : t(
                "{{count}} active contracts were uploaded already signed or have no signing in the product, and are not included in «Signed in the last 30 days».",
                { count: data.activeWithoutSigning },
              )}
        </Text>
      )}
    </Stack>
  );
};

const registerWith = (base: ContractReportQueryDto, filter: Partial<ContractReportQueryDto>): string =>
  `${urls.app.contracts.viewAll}?q=${base64Encode(JSON.stringify({ ...base, statuses: [], ...filter, limit: 10, skip: 0 }))}`;

const SectionCard = ({
  title,
  isRefreshing,
  testId,
  children,
}: {
  title: string;
  isRefreshing: boolean;
  testId: string;
  children: ReactNode;
}) => (
  <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground" data-testid={testId}>
    <Flex align="center" gap="2">
      <Text as="h2" fontWeight="semibold" color="smText">
        {title}
      </Text>
      {isRefreshing && <Spinner size="xs" color="smPrimary" />}
    </Flex>
    {children}
  </Stack>
);

const SeeAll = ({ to, count }: { to: string; count: number }) => {
  const { t } = useTranslation();
  return (
    <Button as={RouterLink} to={to} variant="link" size="sm" colorScheme="teal" alignSelf="flex-start">
      {t("See all {{count}} in the contract register", { count })}
    </Button>
  );
};

const AwaitingTable = ({ rows }: { rows: AwaitingSignatureDto[] }) => {
  const { t } = useTranslation();
  return (
    <>
      {rows.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No contracts are waiting for signing.")}
        </Text>
      ) : (
        <Box overflowX="auto">
          <PanelTable
            headers={[
              String(t("Contract")),
              String(t("Sent")),
              String(t("Signed")),
              String(t("Deadline")),
              String(t("Status")),
            ]}
          >
            {rows.map((row) => (
              <PanelRow key={`${row.contractId}-${row.documentName}-${row.sentAt}`} to={urls.app.contracts.view.documents(row.contractId)}>
                <TitleCell
                  title={row.contractTitle}
                  subtitle={row.deadlinePassed ? String(t("Signing deadline has passed")) : row.documentName}
                  subtitleColor={row.deadlinePassed ? "smError" : undefined}
                />
                <MutedCell>{displayDate(row.sentAt)}</MutedCell>
                <MutedCell>
                  {t("{{signed}} of {{count}} have signed", { signed: row.requiredSigned, count: row.required })}
                </MutedCell>
                <MutedCell>{row.deadline ? displayDate(row.deadline) : "—"}</MutedCell>
                <Td>
                  <SignedDocumentStatusTag status={row.status} size="sm" />
                </Td>
              </PanelRow>
            ))}
          </PanelTable>
        </Box>
      )}
    </>
  );
};

const RecentlySigned = ({ rows }: { rows: RecentlySignedDto[] }) => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure();
  return (
    <Stack spacing="2">
      <Button
        variant="ghost"
        size="sm"
        alignSelf="flex-start"
        px="2"
        ml="-2"
        leftIcon={isOpen ? <FiChevronDown /> : <FiChevronRight />}
        aria-expanded={isOpen}
        aria-controls="recently-signed-list"
        onClick={onToggle}
      >
        {isOpen ? t("Hide the list") : t("Show the list")}
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Box id="recently-signed-list" overflowX="auto">
          {rows.length === 0 ? (
            <Text fontSize="sm" color="smMuted">
              {t("No contracts were signed in the last 30 days.")}
            </Text>
          ) : (
            <PanelTable
              headers={[
                String(t("Contract")),
                String(t("Supplier")),
                String(t("Responsible")),
                String(t("Signing date")),
                String(t("Framework agreement")),
              ]}
            >
              {rows.map((row) => (
                <PanelRow key={row.contractId} to={urls.app.contracts.view.documents(row.contractId)}>
                  <TitleCell title={row.contractTitle} />
                  <MutedCell>{row.supplier ?? "—"}</MutedCell>
                  <MutedCell>{row.responsible ?? "—"}</MutedCell>
                  <MutedCell>{displayDate(row.signedAt)}</MutedCell>
                  <MutedCell>{row.frameworkAgreement ? t("Yes") : t("No")}</MutedCell>
                </PanelRow>
              ))}
            </PanelTable>
          )}
        </Box>
      </Collapse>
    </Stack>
  );
};
